import { useQuery } from '@tanstack/react-query';
import TenantsAPI from '../../namespace/api/tenants';
import apiClient from './apiClient';

export type ExternalServiceHeaders = { [name: string]: string };
export type ExternalServiceType = string; // TODO: add only acceptable type strings?
export type ExternalServiceSecuritySpec = {
	secret?: string;
	tag?: string;
	secureSSL?: boolean;
	hmacEnabled?: boolean;
};

export interface ExternalServiceIntegration {
	type: ExternalServiceType;
	name: string;
	url: string;
	headers: ExternalServiceHeaders;
	securitySpec?: ExternalServiceSecuritySpec;
}

export interface ExternalServiceIntegrationAddRequest {
	type: ExternalServiceType;
	name: string;
	url: string;
	headers?: ExternalServiceHeaders;
	securitySpec?: ExternalServiceSecuritySpec;
}

export interface ExternalServiceIntegrationUpdateRequest {
	url: string;
	headers?: ExternalServiceHeaders;
	securitySpec?: ExternalServiceSecuritySpec;
}

export interface AutofillIntegrationCreateRequest {
	callSequence: string[];
	operation: string;
}

export interface AutofillIntegrationEditRequest {
	callSequence: string[];
	operation: string;
}

export interface AutofillIntegrationDeleteRequest {
	locator: string;
}

export interface AutofillIntegrationResponse {
	calls: AutofillCallResponse[];
}

export interface AutofillCallResponse {
	locator: string;
	operation: string;
	callSequence: [string];
}

export interface ExternalServiceIntegrationsResponse {
	externalServiceIntegrations: ExternalServiceIntegration[];
}

const getIntegrations = async (authToken: string) => {
	const res = await apiClient.get<ExternalServiceIntegrationsResponse>(
		'/integrations',
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const addIntegration = async (
	request: ExternalServiceIntegrationAddRequest,
	authToken: string,
) => {
	const res = await apiClient.post<ExternalServiceIntegrationsResponse>(
		'/integrations',
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const editIntegration = async (
	request: ExternalServiceIntegrationUpdateRequest,
	integrationName: string,
	authToken: string,
) => {
	const res = await apiClient.put<ExternalServiceIntegrationsResponse>(
		`/integrations/${integrationName}`,
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const deleteIntegration = async (
	integrationName: string,
	authToken: string,
) => {
	const res = await apiClient.delete<ExternalServiceIntegrationsResponse>(
		`/integrations/${integrationName}`,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const getDataAutofills = async (authToken: string) => {
	const res = await apiClient.get<AutofillIntegrationResponse>(
		'/integrations/autofill',
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const addUpdateDataAutofill = async (
	request: AutofillIntegrationCreateRequest,
	authToken: string,
) => {
	const res = await apiClient.put<ExternalServiceIntegrationsResponse>(
		'/integrations/autofill',
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

const deleteDataAutofill = async (locator: string, authToken: string) => {
	const res = await apiClient.delete<void>(
		`/integrations/autofill/${locator}`,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

export const QUERY_KEYS = {
	WEBHOOKS: 'webhooks',
};

// Queries
const useExternalRaterIntegrations = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	return useQuery(queryKey, async () => {
		const response = tenantHostname
			? await TenantsAPI.getIntegrations(tenantHostname)
			: await getIntegrations(token);
		const externalRaters = response.externalServiceIntegrations.filter(
			(i) => i.type === 'external_rater',
		);
		return externalRaters;
	});
};

const useWebhookIntegrations = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	return useQuery(queryKey, async () => {
		const response = tenantHostname
			? await TenantsAPI.getIntegrations(tenantHostname)
			: await getIntegrations(token);
		const webhookIntegrations = response.externalServiceIntegrations.filter(
			(i) => i.type === 'webhook',
		);
		return webhookIntegrations;
	});
};

const useDataAutofillIntegrations = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	return useQuery(queryKey, async () => {
		const response = tenantHostname
			? await TenantsAPI.getIntegrations(tenantHostname)
			: await getIntegrations(token);
		const externalRaters = response.externalServiceIntegrations.filter(
			(i) => i.type === 'data_autofill',
		);
		return externalRaters;
	});
};

const useDataAutofillSequences = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	return useQuery(queryKey, async () => {
		const response = tenantHostname
			? await TenantsAPI.getDataAutofills(tenantHostname)
			: await getDataAutofills(token);
		return response;
	});
};

const IntegrationsAPI = {
	getIntegrations,
	addIntegration,
	editIntegration,
	deleteIntegration,
	useExternalRaterIntegrations,
	useWebhookIntegrations,
	getDataAutofills,
	addUpdateDataAutofill,
	deleteDataAutofill,
	useDataAutofillIntegrations,
	useDataAutofillSequences,
};

export default IntegrationsAPI;
